import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useParams } from 'react-router-dom';
import { useGetRestaurantSuppliersApi } from './use-get-restaurant-suppliers-api';

const endpoint = 'ums/v1/cms/restaurant-suppliers';
export const assignRestaurantSupplierEndpointIdentifier = endpoint;

interface BodyType {
  restaurantId: number;
  supplierId: number;
  favorite: boolean;
  isGpoRelation: boolean;
}

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  body: BodyType;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useAssignRestaurantSupplierApi(options?: Options): ReturnType {
  const { supplierId } = useParams<{ supplierId: string }>();
  const { refetch: refetchRestaurantSuppliers } = useGetRestaurantSuppliersApi(parseInt(supplierId), {
    enabled: false,
  });
  const toast = useCustomToast();

  const mutationFn = async ({ body }: TVariables) => {
    try {
      const { data } = await http.post(endpoint, body);

      refetchRestaurantSuppliers();

      toast({
        description: 'Restaurant has been assigned successfully',
        status: 'success',
      });

      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { mutationKey: [endpoint], ...options });
}
